// eslint-disable-next-line no-restricted-imports
import type { MemoizedFunction } from 'lodash';
import memoize from 'lodash/memoize';

import FeatureGates from '@atlaskit/feature-gate-js-client';
import { fg } from '@atlaskit/platform-feature-flags';

import {
	getJiraUIExperienceLayerFrontendExperiment,
	getJiraUIExperienceTenantsLayerFrontendExperiment,
	hasSAINLayerParamValue,
	SAIN_HELLO_LAYER_NAME,
	SAIN_LAYER_NAME,
} from './get-sain-layer-frontend-experiment';

const canRenderLuminaUnmemoized = (): boolean => {
	if (fg('kd_dynamic_layout_in_sain')) {
		return true;
	}

	return false;
};

const isSAINUXUpliftEnabledUnmemoized = (): boolean => {
	if (fg('kd_team_26_sain_borderless_update')) {
		return true;
	}

	const helloLayer = FeatureGates.getLayer(SAIN_HELLO_LAYER_NAME);
	const helloCustomersEnabled = helloLayer.get<boolean>('SAINUXUpliftEnabled', false);

	const customerLayer = FeatureGates.getLayer(SAIN_LAYER_NAME);
	const customersEnabled = customerLayer.get<boolean>('SAINUXUpliftEnabled', false);

	if (helloCustomersEnabled || customersEnabled) {
		return true;
	}

	return false;
};

const isSourcesInShortSAINAnswersEnabledUnmemoized = (): boolean => {
	if (fg('kd_sain_show_sources_in_short_answers')) {
		return true;
	}

	return hasSAINLayerParamValue('sourcesInShortSAINAnswers', 'test');
};

const isZeroQuerySuggestionsV1EnabledUnmemoized = (primaryProduct: string | undefined): boolean => {
	return primaryProduct === 'jira' && fg('kd_jira_prequery_suggestions_v1');
};

const isFullHeightLuminaEnabledUnmemoized = (): boolean => {
	if (fg('kd_enable_full_height_lumina')) {
		return true;
	}
	return false;
};

const isNonPrimaryClickTrackingEnabledUnmemoized = (): boolean => {
	if (fg('kd_track_non_primary_clicks_gate')) {
		return true;
	}

	if (
		FeatureGates.getExperimentValue<string>(
			'kd_track_non_primary_clicks',
			'shouldTrackNonPrimaryClicks',
			'control',
			{ fireExperimentExposure: false },
		) === 'test'
	) {
		return true;
	}

	return false;
};

const triggerNonPrimaryClickTrackingExposure = (): void => {
	try {
		if (!fg('kd_track_non_primary_clicks_gate')) {
			FeatureGates.manuallyLogExperimentExposure('kd_track_non_primary_clicks');
		}
	} catch {
		// Silently fail if FeatureGates is not available (e.g., in test environments)
	}
};

const isNavigationalIntentEnabledUnmemoized = (): boolean => {
	if (fg('kd_sain_enable_navigational_intent_gate')) {
		return true;
	}
	return false;
};

const isLinkListSourcesEnabledUnmemoized = (): boolean => {
	if (fg('kd_sain_link_list_sources')) {
		return true;
	}
	if (hasSAINLayerParamValue('SAINLinkListSources', 'test')) {
		return true;
	}
	return false;
};

const isJiraSAINCardSmartLinkEnabledUnmemoized = ({
	sourceProduct,
}: {
	sourceProduct: string | undefined;
}): boolean => {
	const { serveExperiment: serveJiraFPSNLQNavigationExperiment } =
		getJiraUIExperienceLayerFrontendExperiment({
			abTestId: 'rovo_jira_nin_fps_nlq_navigation_exp',
		});

	const { serveExperiment: serveJiraFPSNLQNavigationTenantsExperiment } =
		getJiraUIExperienceTenantsLayerFrontendExperiment({
			abTestId: 'rovo_jira_nin_fps_nlq_navigation_tenants_exp',
		});

	return (
		sourceProduct === 'jira' &&
		(fg('kd_jira_sain_card_smartlink_fg') ||
			serveJiraFPSNLQNavigationExperiment ||
			serveJiraFPSNLQNavigationTenantsExperiment)
	);
};

export const SAINFeatureControl: {
	/** Controls whether frontend can parse and render lumina-formatted answers */
	canRenderLumina: (() => boolean) & MemoizedFunction;
	isSAINUXUpliftEnabled: (() => boolean) & MemoizedFunction;
	isFullHeightLuminaEnabled: (() => boolean) & MemoizedFunction;
	isSourcesInShortSAINAnswersEnabled: (() => boolean) & MemoizedFunction;
	isZeroQuerySuggestionsV1Enabled: ((primaryProduct: string | undefined) => boolean) & MemoizedFunction;
	isNonPrimaryClickTrackingEnabled: (() => boolean) & MemoizedFunction;
	triggerNonPrimaryClickTrackingExposure: () => void;
	isJiraSAINCardSmartLinkEnabled: (({
		sourceProduct,
	}: {
		sourceProduct: string | undefined;
	}) => boolean) &
		MemoizedFunction;
	isNavigationalIntentEnabled: (() => boolean) & MemoizedFunction;
	isLinkListSourcesEnabled: (() => boolean) & MemoizedFunction;
} = {
	canRenderLumina: memoize(canRenderLuminaUnmemoized),
	isSAINUXUpliftEnabled: memoize(isSAINUXUpliftEnabledUnmemoized),
	isFullHeightLuminaEnabled: memoize(isFullHeightLuminaEnabledUnmemoized),
	isSourcesInShortSAINAnswersEnabled: memoize(isSourcesInShortSAINAnswersEnabledUnmemoized),
	isZeroQuerySuggestionsV1Enabled: memoize(isZeroQuerySuggestionsV1EnabledUnmemoized),
	isNonPrimaryClickTrackingEnabled: memoize(isNonPrimaryClickTrackingEnabledUnmemoized),
	triggerNonPrimaryClickTrackingExposure: triggerNonPrimaryClickTrackingExposure,
	isJiraSAINCardSmartLinkEnabled: memoize(isJiraSAINCardSmartLinkEnabledUnmemoized),
	isNavigationalIntentEnabled: memoize(isNavigationalIntentEnabledUnmemoized),
	isLinkListSourcesEnabled: memoize(isLinkListSourcesEnabledUnmemoized),
};
